import { IngredientTraceCode } from '../types/ingredientTraceCode';
import { UsageRecord } from '../types/usageRecord';
import { LotNumberOption } from '../types/componentRecord';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3003/api';

export const ingredientTraceCodeApiService = {
  /**
   * Fetch all ingredient trace codes from the backend
   */
  fetchIngredientTraceCodes: async (): Promise<IngredientTraceCode[]> => {
    try {
      const response = await fetch(`${API_BASE_URL}/ingredient-trace-codes`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch ingredient trace codes');
      }

      return data.traceCodes;
    } catch (error) {
      console.error('Error fetching ingredient trace codes from API:', error);
      throw error;
    }
  },

  /**
   * Fetch a single ingredient trace code by ID
   */
  fetchIngredientTraceCodeById: async (id: string): Promise<IngredientTraceCode> => {
    try {
      const response = await fetch(`${API_BASE_URL}/ingredient-trace-codes/${id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch ingredient trace code');
      }

      return data.traceCode;
    } catch (error) {
      console.error('Error fetching ingredient trace code from API:', error);
      throw error;
    }
  },

  /**
   * Fetch usage records for a specific ingredient trace code
   * Returns records from the itc_finished_product table
   */
  fetchIngredientTraceCodeUsage: async (id: string): Promise<UsageRecord[]> => {
    const url = `${API_BASE_URL}/ingredient-trace-codes/${id}/usage`;
    console.log('[API Service] Fetching usage records from:', url);

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      console.log('[API Service] Response status:', response.status);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('[API Service] Response data:', data);

      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch usage records');
      }

      console.log('[API Service] Successfully fetched', data.usageRecords.length, 'usage records');
      return data.usageRecords;
    } catch (error) {
      console.error('[API Service] Error fetching usage records from API:', error);
      throw error;
    }
  },

  /**
   * Fetch internal trace codes (as lot numbers) for a specific ingredient name
   */
  fetchTraceCodesByIngredientName: async (ingredientName: string): Promise<LotNumberOption[]> => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/ingredient-trace-codes/by-ingredient/${encodeURIComponent(ingredientName)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch trace codes');
      }

      return data.lotNumbers;
    } catch (error) {
      console.error('Error fetching trace codes from API:', error);
      throw error;
    }
  },

  /**
   * Fetch bulk mapping of trace code IDs to batch lot numbers
   */
  fetchLotNumberMap: async (): Promise<Record<string, string[]>> => {
    try {
      const response = await fetch(`${API_BASE_URL}/ingredient-trace-codes/lot-number-map`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch lot number map');
      }

      return data.lotNumberMap;
    } catch (error) {
      console.error('Error fetching lot number map from API:', error);
      throw error;
    }
  },

  /**
   * Create a new ingredient trace code
   */
  createIngredientTraceCode: async (traceCode: IngredientTraceCode): Promise<IngredientTraceCode> => {
    try {
      const response = await fetch(`${API_BASE_URL}/ingredient-trace-codes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(traceCode)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to create ingredient trace code');
      }

      return data.traceCode;
    } catch (error) {
      console.error('Error creating ingredient trace code:', error);
      throw error;
    }
  },

  /**
   * Update an existing ingredient trace code
   */
  updateIngredientTraceCode: async (traceCode: IngredientTraceCode): Promise<IngredientTraceCode> => {
    try {
      const response = await fetch(`${API_BASE_URL}/ingredient-trace-codes/${traceCode.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(traceCode)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to update ingredient trace code');
      }

      return data.traceCode;
    } catch (error) {
      console.error('Error updating ingredient trace code:', error);
      throw error;
    }
  },

  /**
   * Delete an ingredient trace code by ID
   */
  deleteIngredientTraceCode: async (id: string): Promise<void> => {
    try {
      const response = await fetch(`${API_BASE_URL}/ingredient-trace-codes/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to delete ingredient trace code');
      }
    } catch (error) {
      console.error('Error deleting ingredient trace code:', error);
      throw error;
    }
  }
};
